import { MessageStore } from "./MessageStore";
import type { MessageEventMessage } from "./hooks/useInitConversationChatStore";
import { Sse } from "./sse";
import { UploadFileStore } from "./uploadFile";
import { isMessageRead } from "./utils/isRead";
import { sortMessages } from "./utils/sortMessages";
import type { FormValue } from "@/model/form";
import {
  MessagingMessageType,
  type MessagingPayload,
  MessagingStatus,
  type MessagingMessage,
  type MessagingMessagePayloadMedia,
  type MessageFeedbackType,
  type MessagingParticipant,
  type MessagingConversation,
} from "@/model/messaging";
import { mergeSkipError } from "@/utils/rxjs";
import { isNonNullable } from "@deputy-ai/common";
import { BaseStore, isAtom, ListStore } from "@deputy-ai/common/jotai";
import { produce } from "immer";
import {
  type Atom,
  atom,
  type PrimitiveAtom,
  type SetStateAction,
} from "jotai";
import {
  catchError,
  defer,
  EMPTY,
  filter,
  finalize,
  map,
  merge,
  mergeMap,
  of,
  Subject,
  switchMap,
  take,
  takeWhile,
  tap,
  timer,
  type Observable,
} from "rxjs";
import { v4 } from "uuid";

const PAGE_SIZE = 20;
export interface InitConversationChatStoreOptions {
  conversationUid: string;
  conversation?:
    | MessagingConversation
    | Atom<MessagingConversation | undefined>;

  participants?: {
    myself?: MessagingParticipant;
    aiParticipant?: MessagingParticipant;
  };

  sse?: {
    enabled?: boolean;
    realm?: string;
    baseUrl?: string;
  };

  uploadFile?: {
    uploadFileUrl?: string;
  };

  /**
   * @default true
   * */
  loadMessagesOnInit?: boolean;

  loadMessages: (params: { page: number; pageSize: number }) => Observable<{
    messages: MessagingMessage[];
    total: number;
  }>;
  createNewSendMessage?: () => Omit<MessagingMessage, "uid">;
  createNewPendingMessage?: () => Omit<MessagingMessage, "uid">;
  sendMessage?: (args: {
    message: MessagingMessage;
    payload: MessagingPayload;
  }) => Observable<{
    message: MessagingMessage;
    taskUid?: string;
  }>;
  submitForm?: (args: {
    messageUid: string;
    formValues: Record<string, FormValue>;
  }) => Observable<{
    taskUid?: string;
  }>;
  sendMessageFeedback?: (args: {
    messageUid: string;
    feedbackType: MessageFeedbackType;
  }) => Observable<{
    message: MessagingMessage;
  }>;

  batchSetMessageStatus?: (args: {
    deliveredStatus: Record<string, MessagingStatus>;
    messageUids: string[];
  }) => Observable<any>;
}

export type ConversationChatStoreOptions = Omit<
  InitConversationChatStoreOptions,
  "conversationUid"
>;
export class ConversationChatStore extends BaseStore {
  conversationUid: string;

  options: PrimitiveAtom<ConversationChatStoreOptions>;

  readonly #myselfExternalId = atom((get) => {
    const options = get(this.options);

    return options.participants?.myself?.externalId;
  });

  conversation = atom((get) => {
    const { conversation } = get(this.options);

    if (isAtom(conversation)) {
      return get(conversation);
    }

    return conversation;
  });

  messageStores = atom<MessageStore[]>((get) => {
    return get(this.#messageStoreListStore.list);
  });

  messages = atom<MessagingMessage[]>((get) => {
    const messageStoreList = get(this.messageStores);
    return messageStoreList.map((messageStore) => get(messageStore.message));
  });

  isLoadingMessages = atom(false);
  isLoadedMessages = atom(false);
  noMoreMessages = atom(false);

  isSending = atom(false);

  sse = atom<Sse | undefined>();
  isPendingMessage = atom((get) => {
    const sse = get(this.sse);

    return !!sse;
  });

  canSendMessage = atom((get) => {
    const isSending = get(this.isSending);
    const isPendingMessage = get(this.isPendingMessage);
    return !isSending && !isPendingMessage;
  });

  realtimeChannelId = atom(() => {
    return `conversation._.${this.conversationUid}`;
  });

  unreadCount = atom((get) => {
    const myselfExternalId = get(this.#myselfExternalId);
    if (!myselfExternalId) {
      return 0;
    }

    const messages = get(this.messages);

    const unreadCount = messages.filter((message) => {
      return !isMessageRead({
        deliveredStatus: message.payload.deliveredStatus,
        myselfExternalId,
      });
    }).length;

    return unreadCount;
  });

  readonly uploadFileStore: UploadFileStore;

  readonly #receiveMessage$ = new Subject<{
    message: MessagingMessage;
  }>();

  readonly #messageStoreListStore: ListStore<MessageStore>;
  readonly #loadMessages$ = new Subject<void>();
  readonly #loadMoreMessages$ = new Subject<void>();

  constructor(options: InitConversationChatStoreOptions) {
    super();

    const { conversationUid, ...restOptions } = options;

    this.conversationUid = conversationUid;

    this.options = atom(restOptions);

    this.#messageStoreListStore = new ListStore<MessageStore>({
      idKey: "messageUid",
    });

    this.uploadFileStore = new UploadFileStore({
      uploadFileUrl: atom((get) => {
        const options = get(this.options);
        return options.uploadFile?.uploadFileUrl;
      }),
    });
  }

  getOptions() {
    const options = this.get(this.options);
    return options;
  }

  setOptions(options: SetStateAction<ConversationChatStoreOptions>) {
    this.set(this.options, options);
  }

  init() {
    return mergeSkipError(
      this.#handleLoadMessages(),
      this.#handleSse(),
      this.#handleLoadMessagesOnInit()
    );
  }

  loadMessages() {
    this.#loadMessages$.next();
  }

  loadMore() {
    this.#loadMoreMessages$.next();
  }

  loadMessagesIfNotLoaded() {
    const isLoadedMessages = this.get(this.isLoadedMessages);
    const isLoadingMessages = this.get(this.isLoadingMessages);

    if (isLoadedMessages || isLoadingMessages) {
      return;
    }

    this.loadMessages();
  }

  sendNewMessage(args: { content: string }) {
    const { content } = args;

    return this.#sendMessage(() => {
      if (!content.trim()) {
        throw new Error("message is empty");
      }

      // add pending message
      const newMessage = this.#createNewMessage({ content });
      const messageStore = this.#createMessageStore({
        message: newMessage,
      });

      return messageStore;
    });
  }

  sendNewFile(args: { file: File; fileType: MessagingMessageType }) {
    const { file, fileType } = args;

    return this.#sendMessage(() => {
      const newMessage = this.#createNewMessage({
        content: "",
        media: {
          mediaName: file.name,
          mediaSize: file.size.toString(),
          mediaType: fileType,
        },
      });
      const messageStore = this.#createMessageStore({
        message: newMessage,
        file,
      });

      return messageStore;
    });
  }

  sendMessageFeedback(args: {
    messageUid: string;
    feedbackType: MessageFeedbackType;
  }) {
    const { sendMessageFeedback } = this.getOptions();
    if (!sendMessageFeedback) {
      throw new Error("sendMessageFeedback is required");
    }
    const { messageUid, feedbackType } = args;
    return sendMessageFeedback({ messageUid, feedbackType }).pipe(
      tap(({ message }) => {
        this.updateMessage(messageUid, (prev) => ({
          ...prev,
          ...message,
        }));
      })
    );
  }

  submitForm(args: {
    messageUid: string;
    formValues: Record<string, FormValue>;
  }) {
    const { submitForm } = this.getOptions();
    if (!submitForm) {
      throw new Error("submitForm is required");
    }
    const { messageUid, formValues } = args;
    return submitForm({ messageUid, formValues }).pipe(
      tap(({ taskUid }) => {
        if (taskUid) {
          this.#createSse(taskUid);
        }
      })
    );
  }

  resendMessage(messageUid: string) {
    return this.#sendMessage(() => {
      const messageStore = this.#messageStoreListStore.getItem(messageUid);
      if (!messageStore) {
        throw new Error(`message not found: ${messageUid}`);
      }

      return messageStore;
    });
  }

  updateMessagesStatus(args: {
    deliveredStatus: Record<string, MessagingStatus>;
    messageUids: string[];
  }) {
    const { deliveredStatus, messageUids } = args;

    const { batchSetMessageStatus } = this.getOptions();
    if (!batchSetMessageStatus) {
      throw new Error("batchSetMessageStatus is required");
    }

    return batchSetMessageStatus({ deliveredStatus, messageUids }).pipe(
      tap(() => {
        this.#setMessagesStatus({ deliveredStatus, messageUids });
      })
    );
  }

  updateMessage(messageUid: string, message: SetStateAction<MessagingMessage>) {
    const messageStore = this.#messageStoreListStore.getItem(messageUid);
    if (!messageStore) {
      if (process.env.NODE_ENV === "development") {
        console.warn("message not found", messageUid);
      }
      return;
    }
    messageStore.updateMessage(message);
  }

  handleMessageEvent(message: MessageEventMessage) {
    const sse = this.get(this.sse);

    if (message.taskUid === sse?.taskUid) {
      this.#removeSse();
    }

    this.#receiveMessage$.next({ message });

    return this.#addMessage(message);
  }

  handleMessagesStatusEvent(args: {
    deliveredStatus: Record<string, MessagingStatus>;
    messageUids: string[];
  }) {
    const { deliveredStatus, messageUids } = args;
    this.#setMessagesStatus({ deliveredStatus, messageUids });
  }

  #addMessage(message: MessagingMessage) {
    const messageStore = this.#messageStoreListStore.getItem(message.uid);
    if (messageStore) {
      messageStore.updateMessage((prev) => ({
        ...prev,
        ...message,
      }));
      return;
    }

    const newMessageStore = this.#createMessageStore({ message });

    this.#messageStoreListStore.pushItem(newMessageStore);

    this.#messageStoreListStore.sortItems((a, b) => {
      const aMessage = this.get(a.message);
      const bMessage = this.get(b.message);

      return sortMessages(aMessage, bMessage);
    });
  }

  #removeMessage(uid: string) {
    this.#messageStoreListStore.removeItem(uid);
  }

  #sendMessage(createMessageStore: () => MessageStore) {
    return defer(() => {
      const canSendMessage = this.get(this.canSendMessage);
      if (!canSendMessage) {
        throw new Error("can not send message");
      }

      this.set(this.isSending, true);

      const messageStore = createMessageStore();
      messageStore.markAsSending();

      return of(messageStore);
    }).pipe(
      tap((messageStore) => {
        this.#messageStoreListStore.upsertItem(
          messageStore.messageUid,
          messageStore
        );
      }),
      mergeMap((messageStore) => {
        // upload file
        return defer(() => {
          const { file, uploadFileObjectName } = messageStore;
          if (file && !uploadFileObjectName) {
            return this.uploadFileStore
              .uploadFile({
                file,
              })
              .pipe(
                tap(({ objectName }) => {
                  messageStore.uploadFileSuccess(objectName);
                })
              );
          }

          return of(null);
        }).pipe(
          // send message to backend
          mergeMap(() => {
            return this.#sendMessageToBackend({ messageStore });
          }),
          tap(({ message, taskUid }) => {
            // remove pending message, add sent message
            this.#removeMessage(messageStore.messageUid);
            this.#addMessage(message);

            // create sse if taskUid is provided
            if (taskUid) {
              this.#createSse(taskUid);
            }

            // set message status to delivered
            const externalId =
              this.getOptions().participants?.myself?.externalId;
            if (externalId) {
              this.updateMessagesStatus({
                deliveredStatus: { [externalId]: MessagingStatus.Delivered },
                messageUids: [message.uid],
              }).subscribe();
            }

            // if (isBotRunning) {
            //   checkRuntimeStatusRef.current = checkRuntimeStatus().subscribe();
            // }
          }),
          catchError((err) => {
            messageStore.markAsFailed();
            throw err;
          })
        );
      }),

      finalize(() => {
        this.set(this.isSending, false);
      })
    );
  }

  #createSse(taskId: string) {
    const { sse: sseOptions } = this.getOptions();
    const { enabled, realm, baseUrl } = sseOptions ?? {};
    if (!enabled) {
      return;
    }

    const sse = new Sse({
      taskUid: taskId,
      realm,
      baseUrl,
    });
    this.set(this.sse, sse);
  }

  #removeSse() {
    const sse = this.get(this.sse);
    if (sse) {
      this.#removeMessage(sse.taskUid);
    }
    this.set(this.sse, undefined);
  }

  #sendMessageToBackend(args: { messageStore: MessageStore }) {
    const { messageStore } = args;

    return defer(() => {
      const { sendMessage } = this.getOptions();

      if (!sendMessage) {
        throw new Error("sendMessage is required");
      }

      const message = this.get(messageStore.message);

      const payload = produce(message.payload, (draft) => {
        const media = draft.media;
        if (
          media?.mediaType === MessagingMessageType.Image ||
          media?.mediaType === MessagingMessageType.File
        ) {
          // backend requires mediaUrl to be the objectName
          media.mediaUrl = messageStore.uploadFileObjectName;
        }
      });

      return sendMessage({
        message,
        payload,
      });
    });
  }

  #createNewMessage(args: {
    content: string;
    media?: MessagingMessagePayloadMedia;
  }): MessagingMessage {
    const { content, media } = args;

    const tmpId = v4();
    const defaultMsg = this.#createDefaultMessage();

    return {
      ...defaultMsg,
      uid: tmpId,
      status: MessagingStatus.Sending,
      payload: {
        ...defaultMsg.payload,
        message: content,
        messageType: (media?.mediaType ??
          MessagingMessageType.Text) as MessagingMessageType,
        media,
      },
    };
  }

  #createDefaultMessage() {
    const { createNewSendMessage } = this.getOptions();

    if (!createNewSendMessage) {
      throw new Error("createNewSendMessage is required");
    }
    return createNewSendMessage();
  }

  #handleLoadMessagesOnInit() {
    const { loadMessagesOnInit = true } = this.getOptions();
    if (!loadMessagesOnInit) {
      return EMPTY;
    }
    return timer(100).pipe(
      take(1),
      tap(() => {
        this.loadMessages();
      })
    );
  }

  #handleLoadMessages() {
    return merge(
      this.#loadMessages$.pipe(
        map(() => {
          return {
            page: 1,
            pageSize: PAGE_SIZE,
          };
        })
      ),
      this.#loadMoreMessages$.pipe(
        map(() => {
          const isLoadingMessages = this.get(this.isLoadingMessages);
          if (isLoadingMessages) {
            console.error("isLoadingMessages");
            return null;
          }

          const messageStores = this.get(this.messageStores);
          const currentTotal = messageStores.length;
          const page = Math.floor(currentTotal / PAGE_SIZE) + 1;
          return {
            page,
            pageSize: PAGE_SIZE,
          };
        }),

        filter(isNonNullable)
      )
    ).pipe(
      switchMap(({ page, pageSize }) => {
        this.set(this.isLoadingMessages, true);

        return this.#loadMessages({ page, pageSize }).pipe(
          tap(({ total }) => {
            this.#updateNoMoreMessages({
              page,
              pageSize,
              total: total ?? 0,
            });
          }),
          tap(({ messages }) => {
            this.set(this.isLoadedMessages, true);

            const messageStores = messages
              .map((message) => {
                // if message already exists, update it
                const existingMessageStore =
                  this.#messageStoreListStore.getItem(message.uid);
                if (existingMessageStore) {
                  existingMessageStore.updateMessage((prev) => ({
                    ...prev,
                    ...message,
                  }));
                  return undefined;
                }

                // if message does not exist, create it
                return this.#createMessageStore({ message });
              })
              .filter(isNonNullable);

            this.#messageStoreListStore.setList((prev) => [
              ...messageStores,
              ...prev,
            ]);
          }),
          finalize(() => {
            this.set(this.isLoadingMessages, false);
          })
        );
      })
    );
  }

  #updateNoMoreMessages(args: {
    page: number;
    pageSize: number;
    total: number;
  }) {
    const { page, pageSize, total } = args;
    const noMoreMessages = page * pageSize >= total;
    this.set(this.noMoreMessages, noMoreMessages);
  }

  #handleSse() {
    return this.fromAtom(this.sse).pipe(
      switchMap((sse) => {
        if (!sse) {
          return EMPTY;
        }

        // add pending message
        this.#addPendingMessage({
          uid: sse.taskUid,
        });

        return sse.start().pipe(
          map(({ messageText, message }) => {
            if (message) {
              this.#removeSse();
              this.#addMessage(message);
              return false;
            }

            const messageStore = this.#messageStoreListStore.getItem(
              sse.taskUid
            );

            if (!messageStore) {
              return;
            }

            messageStore.updateMessage((prev) =>
              produce(prev, (draft) => {
                draft.payload.message = messageText;
              })
            );
          }),
          takeWhile((result) => {
            return result !== false;
          }),
          catchError((err) => {
            console.error(err);
            this.#removeSse();
            return EMPTY;
          }),
          finalize(() => {
            this.#removeSse();
          })
        );
      })
    );
  }

  #addPendingMessage(args: { uid: string }) {
    const { uid } = args;

    const { createNewPendingMessage } = this.getOptions();
    if (!createNewPendingMessage) {
      throw new Error("createNewPendingMessage is required");
    }
    const defaultMsg = createNewPendingMessage();
    const pendingMsg = {
      ...defaultMsg,
      uid,
      status: MessagingStatus.Typing,
    };

    this.#addMessage(pendingMsg);
  }

  // eslint-disable-next-line @typescript-eslint/class-methods-use-this
  #createMessageStore(args: { message: MessagingMessage; file?: File }) {
    const { message, file } = args;
    return new MessageStore({ message, file });
  }

  #loadMessages(args: { page: number; pageSize: number }) {
    const { page, pageSize } = args;
    const { loadMessages } = this.getOptions();

    return loadMessages({ page, pageSize });
  }

  #setMessagesStatus(args: {
    deliveredStatus: Record<string, MessagingStatus>;
    messageUids: string[];
  }) {
    const { deliveredStatus, messageUids } = args;

    messageUids.forEach((uid) => {
      try {
        this.updateMessage(uid, (prev) =>
          produce(prev, (draft) => {
            draft.payload.deliveredStatus = {
              ...draft.payload.deliveredStatus,
              ...deliveredStatus,
            };
          })
        );
      } catch (err) {
        console.error(err);
      }
    });
  }
}
